/**
 * Converts Japanese number words (kanji and hiragana) to Roman numerals
 * Handles common patterns in employee IDs, dates, and monetary amounts
 */

// Map of Japanese number characters to digits
const JAPANESE_DIGITS: Record<string, string> = {
  '零': '0', 'ゼロ': '0', 'れい': '0',
  '一': '1', 'いち': '1', 'イチ': '1',
  '二': '2', 'に': '2', 'ニ': '2',
  '三': '3', 'さん': '3', 'サン': '3',
  '四': '4', 'し': '4', 'よん': '4', 'シ': '4', 'ヨン': '4',
  '五': '5', 'ご': '5', 'ゴ': '5',
  '六': '6', 'ろく': '6', 'ロク': '6',
  '七': '7', 'なな': '7', 'しち': '7', 'ナナ': '7', 'シチ': '7',
  '八': '8', 'はち': '8', 'ハチ': '8',
  '九': '9', 'きゅう': '9', 'く': '9', 'キュウ': '9', 'ク': '9',
  '十': '10', 'じゅう': '10', 'ジュウ': '10',
};

// Map for larger numbers
const JAPANESE_UNITS: Record<string, number> = {
  '十': 10, 'じゅう': 10, 'ジュウ': 10,
  '百': 100, 'ひゃく': 100, 'ヒャク': 100,
  '千': 1000, 'せん': 1000, 'セン': 1000,
  '万': 10000, 'まん': 10000, 'マン': 10000,
};

// Common date/time patterns
const DATE_PATTERNS = [
  // Year patterns
  { pattern: /(\d+|[一二三四五六七八九十千]+)年/g, replacement: (match: string, p1: string) => convertJapaneseNumber(p1) + '年' },
  // Month patterns
  { pattern: /(\d+|[一二三四五六七八九十]+)月/g, replacement: (match: string, p1: string) => convertJapaneseNumber(p1) + '月' },
  // Day patterns
  { pattern: /(\d+|[一二三四五六七八九十]+)日/g, replacement: (match: string, p1: string) => convertJapaneseNumber(p1) + '日' },
];

// Convert individual Japanese number to Roman numeral
function convertJapaneseNumber(japaneseNum: string): string {
  // If already a number, return as is
  if (/^\d+$/.test(japaneseNum)) {
    return japaneseNum;
  }

  // Simple digit conversion
  if (JAPANESE_DIGITS[japaneseNum]) {
    return JAPANESE_DIGITS[japaneseNum];
  }

  // Handle compound numbers (e.g., 二十五 = 25)
  let result = 0;
  let current = 0;

  for (let i = 0; i < japaneseNum.length; i++) {
    const char = japaneseNum[i];

    if (JAPANESE_DIGITS[char]) {
      const digit = parseInt(JAPANESE_DIGITS[char]);
      if (digit === 10) {
        result += current === 0 ? 10 : current * 10;
        current = 0;
      } else {
        current = digit;
      }
    } else if (JAPANESE_UNITS[char]) {
      const unit = JAPANESE_UNITS[char];
      result += current === 0 ? unit : current * unit;
      current = 0;
    }
  }

  result += current;
  return result > 0 ? result.toString() : japaneseNum;
}

/**
 * Convert Japanese numbers in text to Roman numerals
 * @param text The text containing Japanese numbers
 * @returns Text with numbers converted to Roman numerals
 */
export function convertJapaneseNumbersToRoman(text: string): string {
  let converted = text;

  // Apply date patterns
  DATE_PATTERNS.forEach(({ pattern, replacement }) => {
    converted = converted.replace(pattern, replacement as any);
  });

  // Convert standalone numbers
  const numberPattern = /[零一二三四五六七八九十百千万]+/g;
  converted = converted.replace(numberPattern, (match) => {
    const romanNum = convertJapaneseNumber(match);
    return romanNum !== match ? romanNum : match;
  });

  // Convert hiragana numbers in common contexts
  const hiraganaPatterns = [
    // Employee/ID numbers often spelled out
    /社員番号[はが]?[：:]?\s*([ぁ-んァ-ン\s]+)/g,
    /ID[はが]?[：:]?\s*([ぁ-んァ-ン\s]+)/g,
  ];

  hiraganaPatterns.forEach(pattern => {
    converted = converted.replace(pattern, (match, numbers) => {
      const digits = numbers.split(/\s+/)
        .map((word: string) => JAPANESE_DIGITS[word] || word)
        .join('');
      return match.replace(numbers, digits);
    });
  });

  return converted;
}

// Helper function to check if text contains Japanese numbers
export function containsJapaneseNumbers(text: string): boolean {
  const patterns = [
    /[零一二三四五六七八九十百千万]+/,
    /[ぁ-ん]+/,
  ];

  return patterns.some(pattern => {
    const matches = text.match(pattern);
    if (matches) {
      return matches.some(match =>
        Object.keys(JAPANESE_DIGITS).includes(match) ||
        Object.keys(JAPANESE_UNITS).includes(match)
      );
    }
    return false;
  });
}